import { HttpClient } from '@angular/common/http';
import {
    EntityActionFactory,
    EntityCacheDispatcher, EntityOp,
    HttpUrlGenerator, Logger, MergeStrategy, QueryParams
} from '@ngrx/data';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Observable, Subscription } from 'rxjs/index';
import { map, withLatestFrom } from 'rxjs/operators';
import { BsModalAlertComponent } from '../../../core/modal/modal-alert.component';
import { WebsocketBrokerService } from '../../../core/services/websocket-broker.service';
import { WebSocketEntityDataServiceBase } from '../../../state/entity/web-socket-entity-data-service-base';
import { Appointment } from '../../appointments/state/appointment.model';
import { dateAppointmentEntityMetadata } from '../../appointments/state/date-appointment.entity';
import { DateAppointmentService } from '../../appointments/state/date-appointment.service';
import { patientAppointmentEntityMetadata } from '../../patients/state/patient-appointment.entity';
import { PatientAppointmentService } from '../../patients/state/patient-appointment.service';
import { remindersReportEntityMetadata } from './reminders-report.entity';

export const entityActionFactory = new EntityActionFactory();

/**
 * This is the base data service to get reminders report (called by NgRx Effects to make actuall calls to the backend).
 * This must be specialized e.g. to get AppointmentList entity data.
 */
export abstract class RemindersReportEntityDataServiceBase<T> extends WebSocketEntityDataServiceBase<any> {
    subscriptions: Subscription = new Subscription();

    constructor(http: HttpClient,
                httpUrlGenerator: HttpUrlGenerator,
                logger: Logger,
                public entityCacheDispatcher: EntityCacheDispatcher,
                public websocketBrokerService: WebsocketBrokerService,
                public dateAppointmentService: DateAppointmentService,
                public patientAppointmentService: PatientAppointmentService,
                entityName: string,
                public webSocketTopic: string,
                public modalService: BsModalService) {
        super(entityName, http, httpUrlGenerator);
    }

    getWithQuery(parameters: string | QueryParams): Observable<Array<any>> {
        return super.getWithQuery(parameters);
    }

    subscribeWebSocketJobWithBody(initialBody: any): void {
        // NOTE: Since we're not subscribing to the result inside an NgRx Effect we need to subscribe here and manage the
        //       subscription.
        const websocketBrokerServiceSubscription =
            this.websocketBrokerService.getRemoteObservable(this.webSocketTopic, initialBody)
                .pipe(
                    withLatestFrom(this.dateAppointmentService.entities$),
                    withLatestFrom(this.patientAppointmentService.entities$),
                    map(([[payload, appointments], patientAppointments]) => {
                        if (payload.error !== undefined) {
                            const message = `Failed getting Reminder Report for the selected appointment (${payload.error})`;
                            const modalRef = this.modalService
                                .show(BsModalAlertComponent, {
                                    initialState:
                                        {
                                            title: 'Error',
                                            message,
                                            okLabel: 'OK'
                                        }
                                });
                            const data: any = [];
                            data.refreshing = false;
                            const entityAction = entityActionFactory.create<Array<Appointment>>(
                                remindersReportEntityMetadata.entityName,
                                EntityOp.QUERY_MANY_SUCCESS,
                                data,
                                {
                                    tag: 'RemindersReportEffect',
                                    mergeStrategy: MergeStrategy.OverwriteChanges
                                }
                            );
                            this.entityCacheDispatcher.dispatch(entityAction);
                        } else {
                            const updatedAppointments: Array<Appointment> = [];
                            if (appointments !== undefined && appointments.length > 0 ||
                                patientAppointments !== undefined && patientAppointments.length > 0) {
                                const entityName = (patientAppointments === undefined ||
                                    (patientAppointments !== undefined && patientAppointments.length === 0)) ?
                                    dateAppointmentEntityMetadata.entityName : patientAppointmentEntityMetadata.entityName;

                                if (entityName === dateAppointmentEntityMetadata.entityName && payload.appointment !== undefined) {
                                    appointments.forEach(appointment => {
                                        if (appointment.appointmentId === +payload.appointment.appointmentId) {
                                            const selectAppointment: Appointment = {...appointment};
                                            selectAppointment.reminderReportLoaded = payload.appointment.reminderReportLoaded;
                                            if (payload.appointment.reminders !== undefined) {
                                                selectAppointment.reminders = payload.appointment.reminders;
                                            }
                                            updatedAppointments.push(selectAppointment);
                                        }
                                    });
                                }

                                if (entityName === patientAppointmentEntityMetadata.entityName && payload.appointment !== undefined) {
                                    patientAppointments.forEach(appointment => {
                                        if (appointment.appointmentId === +payload.appointment.appointmentId) {
                                            const selectAppointment: Appointment = {...appointment};
                                            selectAppointment.reminderReportLoaded = payload.appointment.reminderReportLoaded;
                                            if (payload.appointment.reminders !== undefined) {
                                                selectAppointment.reminders = payload.appointment.reminders;
                                            }
                                            updatedAppointments.push(selectAppointment);
                                        } else {
                                            updatedAppointments.push(appointment);
                                        }
                                    });
                                }

                                if (payload.appointment !== undefined && payload.appointment.reminders !== undefined) {
                                    const entityAction = entityActionFactory.create<Array<Appointment>>(
                                        entityName,
                                        EntityOp.UPSERT_MANY,
                                        updatedAppointments,
                                        {
                                            tag: 'RemindersReportEffect',
                                            mergeStrategy: MergeStrategy.OverwriteChanges
                                        }
                                    );

                                    this.entityCacheDispatcher.dispatch(entityAction);
                                }

                                const data: any = [];
                                data.refreshing = false;
                                const entityLoadingAction = entityActionFactory.create<Array<Appointment>>(
                                    remindersReportEntityMetadata.entityName,
                                    EntityOp.QUERY_MANY_SUCCESS,
                                    data,
                                    {
                                        tag: 'RemindersReportSideEffect',
                                        mergeStrategy: MergeStrategy.OverwriteChanges
                                    }
                                );

                                this.entityCacheDispatcher.dispatch(entityLoadingAction);

                            }
                        }
                    })
                )
                .subscribe();

        this.subscriptions.add(websocketBrokerServiceSubscription);
    }

    unsubscribeWebSocketJob(): void {
        this.websocketBrokerService.unsubscribe(this.webSocketTopic);
        this.subscriptions.unsubscribe();
        this.subscriptions = new Subscription();
    }

    refreshWebSocketJobWithBody(messageBody: any): void {
        this.websocketBrokerService.sendAction('refresh', this.webSocketTopic, messageBody);
    }
}
